import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

// ─── Types ──────────────────────────────────────────────────────

export interface FuelTransaction {
  id: string;
  provider: string;
  external_id: string;
  transaction_at: string;
  vehicle_id: string;
  cost_attributed_vehicle_id: string | null;
  driver_id: string | null;
  provider_driver_name: string | null;
  card_number: string | null;
  liters: number;
  total_sar: number;
  cost_before_vat_sar: number | null;
  vat_sar: number | null;
  fuel_type: string | null;
  station_id: string | null;
  station_name: string | null;
  station_lat: number | null;
  station_lng: number | null;
  km_per_liter: number | null;
  liters_per_100km: number | null;
  odometer_km: number | null;
  is_anomaly: boolean | null;
  cost_attribution_movement_id: string | null;
  vehicle_plate: string | null;
  vehicle_code: string | null;
  attributed_vehicle_plate: string | null;
  attributed_vehicle_code: string | null;
  driver_name: string | null;
  /** Who held this truck on the day of the fill, when the fill matched nobody.
   *  A recorded fact from the assignment history - not today's assignment. */
  assigned_driver_name?: string | null;
}

export interface FuelDashboardStats {
  month_sar: number;
  month_liters: number;
  month_count: number;
  pending_card_movements: number;
  last_import: { committed_at: string; filename: string } | null;
}

export interface ProviderAccount {
  id: string;
  provider: 'petroapp' | 'aldrees';
  account_label: string;
  account_code: string | null;
  legal_entity_id: string;
  active: boolean;
}

export interface UnmatchedPlate {
  plate_key: string;
  plate_raw: string;
  sample_brand: string | null;
  sample_model: string | null;
  sample_count: number;
}
export interface UnmatchedDriver {
  alias_name: string;
  sample_count: number;
  is_generic_placeholder: boolean;
}

export interface ImportPreview {
  import_id: string;
  detected_provider: string;
  period_from: string | null;
  period_to: string | null;
  warnings: string[];
  summary: {
    total_rows: number;
    new_rows: number;
    duplicate_rows: number;
    unmatched_vehicles: number;
    unmatched_drivers: number;
    card_movements_flagged: number;
    unique_unmatched_plates: UnmatchedPlate[];
    unique_unmatched_drivers: UnmatchedDriver[];
  };
}

export interface CardMovement {
  id: string;
  fuel_transaction_id: string;
  provider: string;
  card_number: string;
  assigned_vehicle_id: string | null;
  assigned_vehicle_plate: string | null;
  actual_vehicle_id: string | null;
  actual_vehicle_plate: string | null;
  actual_driver_name: string | null;
  movement_type: string;
  status: string;
  detected_at: string;
  authorization_reason: string | null;
  resolution_notes: string | null;
  transaction_at: string;
  total_sar: number;
  liters: number;
  station_name: string | null;
  provider_driver_name: string | null;
}

// ─── Hooks ──────────────────────────────────────────────────────

export function useFuelDashboard() {
  return useQuery({
    queryKey: ['fuel', 'dashboard'],
    queryFn: async () => {
      const { data } = await apiClient.get<FuelDashboardStats>('/api/v1/fuel/dashboard');
      return data;
    },
    staleTime: 60_000,
  });
}

export function useFuelTransactions(filters: Record<string, any> = {}) {
  return useQuery({
    queryKey: ['fuel', 'transactions', filters],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        items: FuelTransaction[];
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
      }>('/api/v1/fuel-transactions', { params: filters });
      return data;
    },
    staleTime: 30_000,
  });
}

export function useFuelTransaction(id: string | null) {
  return useQuery({
    queryKey: ['fuel', 'transaction', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/api/v1/fuel-transactions/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useProviderAccounts() {
  return useQuery({
    queryKey: ['fuel', 'provider-accounts'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: ProviderAccount[] }>('/api/v1/fuel-provider-accounts');
      return data.items;
    },
    staleTime: 5 * 60_000,
  });
}

export function useFuelImports() {
  return useQuery({
    queryKey: ['fuel', 'imports'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: any[] }>('/api/v1/fuel-imports');
      return data.items;
    },
  });
}

export function useImportPreview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { file: File; provider_account_id: string; parser_hint?: string }) => {
      const fd = new FormData();
      fd.append('file', args.file);
      fd.append('provider_account_id', args.provider_account_id);
      fd.append('parser_hint', args.parser_hint ?? 'auto');
      const { data } = await apiClient.post<ImportPreview>('/api/v1/fuel-imports/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fuel', 'imports'] });
    },
  });
}

export function useImportCommit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      import_id: string;
      driver_resolutions: Record<string, string | null>;
      vehicle_resolutions: Record<string, string | null>;
      new_vehicles: any[];
      new_drivers: any[];
    }) => {
      const { import_id, ...body } = args;
      const { data } = await apiClient.post(`/api/v1/fuel-imports/${import_id}/commit`, body);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fuel'] });
    },
  });
}

export function useCardMovements(status: 'pending' | 'resolved' | 'all' = 'pending') {
  return useQuery({
    queryKey: ['fuel', 'card-movements', status],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: CardMovement[] }>('/api/v1/fuel-card-movements', { params: { status } });
      return data.items;
    },
  });
}

export function useResolveCardMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      movement_type: 'emergency_swap' | 'fraud_confirmed' | 'data_error';
      actual_vehicle_id?: string | null;
      actual_driver_id?: string | null;
      authorization_reason: string;
      resolution_notes?: string | null;
    }) => {
      const { id, ...body } = args;
      const { data } = await apiClient.post(`/api/v1/fuel-card-movements/${id}/resolve`, body);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fuel', 'card-movements'] });
      qc.invalidateQueries({ queryKey: ['fuel', 'transactions'] });
      qc.invalidateQueries({ queryKey: ['fuel', 'dashboard'] });
    },
  });
}
